"use client"

import { useEffect, useState } from "react"

import type { supabaseBrowser } from "@/lib/supabase/client"
import type { AuthChipUser } from "@/components/marketing/auth-chip"

export type MarketingAuth = { authed: boolean; user?: AuthChipUser }

// Derived from the browser client so the type stays in lockstep with the SDK.
type SupabaseSession = Awaited<
  ReturnType<ReturnType<typeof supabaseBrowser>["auth"]["getSession"]>
>["data"]["session"]

// @supabase/ssr persists the browser session in cookies named
// sb-<project-ref>-auth-token (chunked variants add .0/.1 suffixes). Presence
// of any such cookie is the cheap "might be signed in" signal; absence means
// signed out for certain, so the SDK never needs to load.
export function hasPersistedSupabaseSession(): boolean {
  try {
    return /(?:^|;\s*)sb-[^=;]*auth-token[^=;]*=/.test(document.cookie)
  } catch {
    return false
  }
}

// Resolves the signed-in state in the BROWSER instead of the server, so the
// marketing layout (and therefore every marketing/content page under it) has no
// `auth()`/`cookies()` call and can be statically prerendered + CDN-cached.
// getSession() reads the persisted Supabase session from local storage with no
// network round-trip, so the masthead settles to the signed-in chip almost
// immediately after hydration; onAuthStateChange keeps it live across
// sign-in/out within the session. The static HTML ships the signed-out view,
// which is the correct canonical state for the public marketing site.
//
// The Supabase browser client loads LAZILY, and only when a persisted session
// cookie exists (perf, 2026-07-23): a signed-out visitor - all paid ad
// traffic - never downloads the supabase-js chunk just to render the
// signed-out masthead the static HTML already ships. A signed-in user gets
// the chip one tick later, same as the previous post-hydration settle.
export function useMarketingAuth(): MarketingAuth {
  const [auth, setAuth] = useState<MarketingAuth>({ authed: false })

  useEffect(() => {
    if (!hasPersistedSupabaseSession()) return

    let active = true
    let unsubscribe: (() => void) | undefined

    void import("@/lib/supabase/client").then(({ supabaseBrowser }) => {
      if (!active) return
      const supabase = supabaseBrowser()

      supabase.auth.getSession().then(({ data: { session } }) => {
        if (active) setAuth(toMarketingAuth(session))
      })

      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, session) => {
        setAuth(toMarketingAuth(session))
      })
      unsubscribe = () => subscription.unsubscribe()
    })

    return () => {
      active = false
      unsubscribe?.()
    }
  }, [])

  return auth
}

function toMarketingAuth(session: SupabaseSession): MarketingAuth {
  const user = session?.user
  if (!user) return { authed: false }
  const meta = user.user_metadata ?? {}
  return {
    authed: true,
    user: {
      name: (meta.name as string | undefined) ?? (meta.full_name as string | undefined) ?? null,
      email: user.email ?? null,
      image: (meta.avatar_url as string | undefined) ?? (meta.picture as string | undefined) ?? null,
    },
  }
}
